import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Edit2, Trash2, Eye, EyeOff, ChevronDown, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  "IA y futuro del trabajo",
  "Empresa y estrategia",
  "Fundraising y narrativa",
  "Geopolítica empresarial",
  "Liderazgo y construcción",
  "Notas breves",
];

const EMPTY_FORM = { title: "", content: "", excerpt: "", category: CATEGORIES[0], status: "draft", image_url: "" };

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Login ──
function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", r.data.token);
      onLogin(r.data.token);
      toast.success("Acceso correcto.");
    } catch {
      toast.error("Contraseña incorrecta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <p className="label-mono text-muted-foreground mb-2">Panel de administración</p>
        <h1 className="font-serif text-3xl mb-10">Eduardo Delgado</h1>
        <form onSubmit={submit} className="space-y-6">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              className="input-underline w-full pr-10"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
              autoFocus
            />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-0 top-3 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="label-mono bg-primary text-primary-foreground px-8 py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 w-full"
          >
            {loading ? "Accediendo…" : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Post Form Modal ──
function PostModal({ post, onSave, onClose, token }) {
  const [form, setForm] = useState(post || EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      let r;
      if (post?.id) {
        r = await axios.put(`${API}/admin/posts/${post.id}`, form, { headers });
        toast.success("Artículo actualizado.");
      } else {
        r = await axios.post(`${API}/admin/posts`, form, { headers });
        toast.success("Artículo creado.");
      }
      onSave(r.data);
    } catch {
      toast.error("Error al guardar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 pt-16" data-testid="post-modal">
      <div className="bg-background w-full max-w-2xl border border-border p-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-muted-foreground hover:text-foreground">
          <X size={18} />
        </button>
        <h2 className="font-serif text-2xl mb-8">{post?.id ? "Editar artículo" : "Nuevo artículo"}</h2>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="label-mono text-muted-foreground block mb-2">Título *</label>
            <input className="input-underline w-full" value={form.title} onChange={set("title")} required placeholder="Título del artículo" data-testid="post-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="label-mono text-muted-foreground block mb-2">Categoría</label>
              <select className="input-underline w-full" value={form.category} onChange={set("category")} data-testid="post-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono text-muted-foreground block mb-2">Estado</label>
              <select className="input-underline w-full" value={form.status} onChange={set("status")} data-testid="post-status-select">
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-mono text-muted-foreground block mb-2">Extracto</label>
            <textarea className="input-underline textarea w-full" rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Resumen breve para listados" data-testid="post-excerpt-input" />
          </div>
          <div>
            <label className="label-mono text-muted-foreground block mb-2">Contenido *</label>
            <textarea
              className="input-underline textarea w-full font-sans"
              rows={12}
              value={form.content}
              onChange={set("content")}
              required
              placeholder="Contenido del artículo. Separa los párrafos con una línea en blanco."
              data-testid="post-content-input"
            />
          </div>
          <div>
            <label className="label-mono text-muted-foreground block mb-2">URL de imagen (opcional)</label>
            <input className="input-underline w-full" value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="post-image-input" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="label-mono border border-border px-6 py-3 hover:border-foreground/30 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={loading} data-testid="post-save-btn" className="label-mono bg-primary text-primary-foreground px-8 py-3 hover:bg-primary/90 transition-colors disabled:opacity-60">
              {loading ? "Guardando…" : post?.id ? "Actualizar" : "Publicar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Dashboard ──
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | post object
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/posts`, { headers }),
      axios.get(`${API}/admin/stats`, { headers }),
    ]).then(([p, s]) => {
      setPosts(p.data);
      setStats(s.data);
    }).catch(() => {
      localStorage.removeItem("admin_token");
      setToken(null);
    }).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (tab === "contacts") {
      axios.get(`${API}/admin/contacts`, { headers }).then((r) => setContacts(r.data)).catch(() => {});
    } else if (tab === "subscribers") {
      axios.get(`${API}/admin/subscribers`, { headers }).then((r) => setSubscribers(r.data)).catch(() => {});
    }
  }, [tab, token]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este artículo?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, { headers });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setStats((s) => s ? { ...s, posts: s.posts - 1 } : s);
      toast.success("Artículo eliminado.");
    } catch {
      toast.error("Error al eliminar.");
    }
  };

  const handleSaved = (savedPost) => {
    setPosts((prev) => {
      const idx = prev.findIndex((p) => p.id === savedPost.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = savedPost;
        return next;
      }
      return [savedPost, ...prev];
    });
    setModal(null);
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {modal && (
        <PostModal
          post={modal === "new" ? null : modal}
          onSave={handleSaved}
          onClose={() => setModal(null)}
          token={token}
        />
      )}

      {/* Top bar */}
      <header className="border-b border-border bg-card px-6 md:px-10 h-16 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <p className="font-serif text-lg">Eduardo Delgado</p>
          <span className="label-mono text-muted-foreground hidden sm:block">/ Admin</span>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="label-mono text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors">
          <LogOut size={14} /> Salir
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            {[
              { label: "Total artículos", value: stats.posts },
              { label: "Publicados", value: stats.published },
              { label: "Suscriptores", value: stats.subscribers },
              { label: "Contactos", value: stats.contacts },
            ].map((s) => (
              <div key={s.label} className="bg-card border border-border p-6">
                <p className="label-mono text-muted-foreground mb-2">{s.label}</p>
                <p className="font-serif text-3xl">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-8">
          {[["posts", "Artículos"], ["contacts", "Contactos"], ["subscribers", "Suscriptores"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`label-mono px-5 py-3 border-b-2 transition-colors ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Posts Tab */}
        {tab === "posts" && (
          <div data-testid="admin-posts-tab">
            <div className="flex justify-between items-center mb-6">
              <p className="label-mono text-muted-foreground">{posts.length} artículos</p>
              <button
                onClick={() => setModal("new")}
                data-testid="new-post-btn"
                className="label-mono bg-primary text-primary-foreground px-5 py-2.5 hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Plus size={13} /> Nuevo artículo
              </button>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(n => <div key={n} className="h-14 bg-card animate-pulse" />)}
              </div>
            ) : (
              <div className="border border-border overflow-hidden">
                <table className="w-full text-sm" data-testid="posts-table">
                  <thead className="bg-card">
                    <tr>
                      <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal">Título</th>
                      <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal hidden md:table-cell">Categoría</th>
                      <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal">Estado</th>
                      <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal hidden sm:table-cell">Fecha</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {posts.map((p) => (
                      <tr key={p.id} data-testid={`post-row-${p.id}`} className="hover:bg-card/50 transition-colors">
                        <td className="px-5 py-4 font-medium max-w-xs">
                          <span className="line-clamp-1">{p.title}</span>
                        </td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          <span className="label-mono text-xs text-muted-foreground">{p.category}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`label-mono text-xs px-2 py-1 ${p.status === "published" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                            {p.status === "published" ? "Publicado" : "Borrador"}
                          </span>
                        </td>
                        <td className="px-5 py-4 hidden sm:table-cell label-mono text-muted-foreground text-xs">
                          {formatDate(p.created_at)}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => setModal(p)}
                              data-testid={`edit-post-${p.id}`}
                              className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                              aria-label="Editar"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              data-testid={`delete-post-${p.id}`}
                              className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                              aria-label="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {posts.length === 0 && (
                  <div className="py-12 text-center text-muted-foreground label-mono">No hay artículos. Crea el primero.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {tab === "contacts" && (
          <div data-testid="admin-contacts-tab">
            <p className="label-mono text-muted-foreground mb-6">{contacts.length} mensajes de contacto</p>
            <div className="space-y-4">
              {contacts.length === 0 ? (
                <p className="label-mono text-muted-foreground py-8">No hay mensajes aún.</p>
              ) : contacts.map((c) => (
                <div key={c.id} data-testid={`contact-item-${c.id}`} className="bg-card border border-border p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="label-mono text-muted-foreground text-xs">{c.email} {c.company ? `· ${c.company}` : ""} {c.position ? `· ${c.position}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="label-mono text-xs bg-accent/10 text-accent px-2 py-1">{c.reason}</span>
                      <span className="label-mono text-muted-foreground text-xs">{formatDate(c.created_at)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {tab === "subscribers" && (
          <div data-testid="admin-subscribers-tab">
            <p className="label-mono text-muted-foreground mb-6">{subscribers.length} suscriptores</p>
            <div className="border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr>
                    <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal">Nombre</th>
                    <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal">Email</th>
                    <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal hidden sm:table-cell">Estado Mailchimp</th>
                    <th className="text-left label-mono text-muted-foreground px-5 py-3 font-normal hidden md:table-cell">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {subscribers.map((s) => (
                    <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="hover:bg-card/50">
                      <td className="px-5 py-3">{s.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{s.email}</td>
                      <td className="px-5 py-3 hidden sm:table-cell">
                        <span className={`label-mono text-xs px-2 py-1 ${s.mailchimp_status === "subscribed" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {s.mailchimp_status}
                        </span>
                      </td>
                      <td className="px-5 py-3 label-mono text-muted-foreground text-xs hidden md:table-cell">
                        {formatDate(s.subscribed_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscribers.length === 0 && (
                <div className="py-12 text-center text-muted-foreground label-mono">No hay suscriptores aún.</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
